import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchBrands, fetchSector, fetchSectors, fetchSiteStats } from "@/lib/api";
import { useI18n, pickLocalized } from "@/lib/i18n";
import BrandCard from "@/components/BrandCard";
import Seo from "@/components/Seo";

const FILTERS = [
  { key: "discloses_factories", tKey: "sector_filter_factories" },
  { key: "repair_info", tKey: "sector_filter_repair" },
  { key: "verifiable_certs", tKey: "sector_filter_certs" },
];

export default function SectorBrowse() {
  const { code } = useParams();
  const { t, locale } = useI18n();
  const [sector, setSector] = useState(null);
  const [allSectors, setAllSectors] = useState([]);
  const [siteStats, setSiteStats] = useState(null);
  const [brands, setBrands] = useState([]);
  const [filters, setFilters] = useState(new Set());
  const [sort, setSort] = useState("alpha");

  useEffect(() => {
    fetchSector(code).then((d) => setSector(d.sector));
  }, [code]);
  useEffect(() => {
    fetchSectors().then((d) => setAllSectors(d.sectors || [])).catch(() => {});
    fetchSiteStats().then((d) => setSiteStats(d)).catch(() => {});
  }, []);

  useEffect(() => {
    const filterStr = filters.size > 0 ? Array.from(filters).join(",") : undefined;
    fetchBrands({ sector: code.toUpperCase(), sort, filter: filterStr }).then((d) => setBrands(d.brands || []));
  }, [code, sort, filters]);

  const toggleFilter = (key) => {
    setFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // "Next sector →" — cycles through the sectors list so a visitor
  // can hop between them without going back to the home page.
  const nextSector = useMemo(() => {
    if (!allSectors.length) return null;
    const codes = allSectors.map((s) => s.code);
    const idx = codes.indexOf(code.toUpperCase());
    if (idx === -1) return null;
    return allSectors[(idx + 1) % allSectors.length];
  }, [allSectors, code]);

  // 4-week trend for THIS sector, from `sectors_trend` snapshots.
  const sectorTrend = useMemo(() => {
    const upper = code.toUpperCase();
    return (siteStats?.sectors_trend || [])
      .map((r) => ({ at: r.at, entry: r.sectors?.[upper] }))
      .filter((r) => r.entry);
  }, [siteStats, code]);

  if (!sector) return <div className="bp-container py-24" />;
  const name = pickLocalized(sector.name, locale);

  return (
    <>
      <Seo title={`${name} — brand transparency profiles`} description={pickLocalized(sector.dpp_deadline_note, locale)} canonicalPath={`/sectors/${code.toLowerCase()}`} locale={locale} />
      <section className="border-b" style={{ borderColor: "var(--bp-border)" }}>
        <div className="bp-container py-14">
          <div className="flex items-baseline justify-between gap-4 mb-4">
            <div className="uppercase text-xs tracking-[0.3em]" style={{ color: "var(--bp-text-muted)" }}>Sector</div>
            {nextSector && (
              <Link
                to={`/sectors/${nextSector.code.toLowerCase()}`}
                className="uppercase text-xs tracking-widest bp-link inline-flex items-center gap-1 group"
                data-testid="sector-switcher"
                title={locale === "it" ? `Vai a ${pickLocalized(nextSector.name, locale)}` : `Go to ${pickLocalized(nextSector.name, locale)}`}
              >
                {locale === "it" ? "Prossimo settore" : "Next sector"} · {pickLocalized(nextSector.name, locale)}
                <span className="transition-transform group-hover:translate-x-0.5">→</span>
              </Link>
            )}
          </div>
          <h1 className="font-serif text-5xl md:text-6xl leading-none" data-testid="sector-title">{name}</h1>
          <p className="mt-4 max-w-3xl text-base md:text-lg" style={{ color: "var(--bp-text-secondary)" }}>
            {pickLocalized(sector.dpp_deadline_note, locale)}
          </p>
          <div className="mt-6 flex flex-wrap gap-6 text-sm font-mono" style={{ color: "var(--bp-text-secondary)" }}>
            <span data-testid="sector-total">{sector.total_indexed} brands indexed</span>
            {typeof sector.publish_full_materials_count === "number" && (
              <span data-testid="sector-materials-count">{t.sector_materials_count(sector.publish_full_materials_count)}</span>
            )}
            <span>DPP: {sector.dpp_deadline_year}</span>
          </div>

          {/* 4-week per-sector trend line — item 4 (sector detail). */}
          {sectorTrend.length >= 2 && (() => {
            const values = sectorTrend.map((r) => r.entry.published || 0);
            const maxV = Math.max(1, ...values);
            const W = 180, H = 32, PAD = 3;
            const step = (W - PAD * 2) / (values.length - 1);
            const points = values
              .map((v, i) => `${PAD + i * step},${H - PAD - (v / maxV) * (H - PAD * 2)}`)
              .join(" ");
            const first = sectorTrend[0].at?.slice(0, 10);
            const last = sectorTrend[sectorTrend.length - 1].at?.slice(0, 10);
            const latest = sectorTrend[sectorTrend.length - 1].entry;
            const pct = latest.total > 0 ? Math.round((latest.published / latest.total) * 100) : 0;
            const stroke = pct >= 75 ? "#166534" : pct >= 33 ? "#CA8A04" : "#B45309";
            return (
              <div className="mt-6 flex items-center gap-4" data-testid={`sector-trend-detail-${code.toUpperCase()}`}>
                <svg width={W} height={H} aria-hidden style={{ display: "block" }}>
                  <polyline fill="none" stroke={stroke} strokeWidth="1.75" points={points} />
                </svg>
                <div className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>
                  {locale === "it" ? "Brand pubblicati" : "Brands published"} · {latest.published}/{latest.total}
                  <div className="text-[10px]">{first} → {last}</div>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      <div className="bp-container py-8">
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <span className="uppercase text-xs tracking-widest mr-2" style={{ color: "var(--bp-text-muted)" }}>Filter</span>
          <button
            onClick={() => setFilters(new Set())}
            className={`bp-chip ${filters.size === 0 ? "bp-chip--found" : "bp-chip--notfound"}`}
            data-testid="filter-all"
            aria-pressed={filters.size === 0}
            title={locale === "it" ? "Rimuovi filtri" : "Show all brands"}
          >
            {locale === "it" ? "Tutti i brand" : "All brands"}
          </button>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => toggleFilter(f.key)}
              className={`bp-chip ${filters.has(f.key) ? "bp-chip--found" : "bp-chip--notfound"}`}
              data-testid={`filter-${f.key}`}
              aria-pressed={filters.has(f.key)}
              title={filters.has(f.key) ? (locale === "it" ? "Clicca di nuovo per rimuovere il filtro" : "Click again to remove filter") : undefined}
            >
              {t[f.tKey]}{filters.has(f.key) ? " ✕" : ""}
            </button>
          ))}
          {filters.size > 0 && (
            <button
              onClick={() => setFilters(new Set())}
              className="bp-link text-xs ml-1"
              data-testid="filter-clear"
              title={locale === "it" ? "Rimuovi filtri" : "Clear filters"}
            >
              {locale === "it" ? `rimuovi ${filters.size} filtri` : `clear ${filters.size} filter${filters.size === 1 ? "" : "s"}`} →
            </button>
          )}
          <div className="ml-auto flex gap-1 text-xs font-mono">
            <button
              onClick={() => setSort("alpha")}
              className={`px-3 py-1.5 border ${sort === "alpha" ? "bg-black text-white border-black" : ""}`}
              style={{ borderColor: "var(--bp-border)" }}
              data-testid="sort-alpha"
            >
              {t.sort_alpha}
            </button>
            <button
              onClick={() => setSort("recent")}
              className={`px-3 py-1.5 border ${sort === "recent" ? "bg-black text-white border-black" : ""}`}
              style={{ borderColor: "var(--bp-border)" }}
              data-testid="sort-recent"
            >
              {t.sort_recent}
            </button>
          </div>
        </div>

        {brands.length === 0 ? (
          <div
            className="border p-10 md:p-14 bg-white"
            style={{ borderColor: "var(--bp-border)" }}
            data-testid="sector-empty-state"
          >
            <div className="uppercase text-xs tracking-[0.3em] mb-4" style={{ color: "var(--bp-text-muted)" }}>{t.empty_kicker}</div>
            <div className="font-serif text-3xl leading-tight mb-3">{t.empty_title}</div>
            <p className="text-base leading-relaxed max-w-2xl" style={{ color: "var(--bp-text-secondary)" }}>{t.empty_body}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brands.map((b) => <BrandCard key={b.id} brand={b} />)}
          </div>
        )}
      </div>
    </>
  );
}
